// De stappen die niet vanzelf gaan bij een nieuwe collega.
//
// Joris, 11 sep 2026: "kan je de mailbox van Naomi koppelen, en hoe maken we dat
// onderdeel van onboarding?" De Exchange-kant kan alleen met PowerShell als
// beheerder — dat kan de app niet voor je doen. Wat de app wél kan: de opdracht
// kant-en-klaar geven en de instelling `assistant_users` zelf bijwerken, zodat
// alleen de echt handmatige stap overblijft.
import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Paper, Tooltip, Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import toast from 'react-hot-toast';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

const FRITS_APP_ID = '47ffa52b-9bde-4f23-9c4f-946b636c128e';

const powershell = (email) => `Connect-ExchangeOnline -UserPrincipalName joris@fullframe.nl
$app = "${FRITS_APP_ID}"   # Frits mail-assistent
$grp = (Get-ApplicationAccessPolicy | Where-Object AppId -eq $app).PolicyScopeGroupId
Add-DistributionGroupMember -Identity $grp -Member "${email}"
Test-ApplicationAccessPolicy -Identity "${email}" -AppId $app`;

export default function HandmatigeOnboardingStappen({ email, naam }) {
  const [assistantUsers, setAssistantUsers] = useState(null);
  const [bezig, setBezig] = useState(false);

  useEffect(() => {
    authFetch(`${AI_ENGINE_URL}/api/v1/admin/settings/values?keys=assistant_users`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setAssistantUsers(d?.values?.assistant_users ?? ''))
      .catch(() => setAssistantUsers(''));
  }, []);

  const staatErin = typeof assistantUsers === 'string'
    && email && assistantUsers.toLowerCase().includes(String(email).toLowerCase());

  const kopieer = async (tekst) => {
    try {
      await navigator.clipboard.writeText(tekst);
      toast.success('Gekopieerd');
    } catch { toast.error('Kopiëren lukte niet — selecteer de tekst handmatig'); }
  };

  const zetInAssistantUsers = async () => {
    if (!email) return;
    setBezig(true);
    try {
      const huidig = (assistantUsers || '').split(',').map((x) => x.trim()).filter(Boolean);
      const nieuw = [...huidig, email].join(', ');
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/admin/settings/assistant_users`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: nieuw }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setAssistantUsers(nieuw);
      toast.success('Toegevoegd aan de dagelijkse Frits-run');
    } catch (e) {
      toast.error(`Instellen mislukt: ${e.message}`);
    } finally { setBezig(false); }
  };

  if (!email) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2.5, mt: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>
        Nog handmatig: mailtoegang voor {naam || email}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Zonder deze twee stappen leest Frits de mailbox niet: de dagelijkse triage en het
        zoeken in mail bij een overdracht komen dan leeg terug.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Chip size="small" label="1" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Mailbox in de toegangsgroep van de Frits-app zetten (Exchange, alleen als beheerder)
        </Typography>
        <Tooltip title="Kopieer de PowerShell-opdracht">
          <Button size="small" startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
            onClick={() => kopieer(powershell(email))} sx={{ textTransform: 'none' }}>
            Kopiëren
          </Button>
        </Tooltip>
      </Box>
      <Box component="pre" sx={{
        bgcolor: 'grey.50', p: 1.5, borderRadius: 1, fontSize: '0.75rem',
        overflowX: 'auto', mb: 2, whiteSpace: 'pre-wrap',
      }}>
        {powershell(email)}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip size="small" label="2" />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Meenemen in de dagelijkse Frits-run
        </Typography>
        {assistantUsers === null ? <CircularProgress size={14} /> : staatErin ? (
          <Chip size="small" color="success" label="Staat er al in" />
        ) : (
          <Button size="small" variant="outlined" disabled={bezig} onClick={zetInAssistantUsers}
            sx={{ textTransform: 'none' }}>
            {bezig ? <CircularProgress size={14} /> : 'Toevoegen'}
          </Button>
        )}
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        Stap 1 kan de app niet zelf doen: die vraagt een beheerderssessie in Exchange Online.
        Controleer na afloop met de laatste regel — die moet <b>Granted</b> teruggeven.
      </Alert>
    </Paper>
  );
}
